import React, { useState, useEffect, useContext } from 'react';
import useCollapse from 'react-collapsed';

import {
  Nav,
  Button,
  ActiveTopic,
  CaretIcon,
  List,
  StyledSubList,
  Link,
  SubButton,
} from './TopicNavigation.style';

import WithNewLabel from 'components/styled/WithNewLabel/WithNewLabel';

import { topicsWithSubTopics } from 'topics';
import { EntityContext } from 'context/entityContext';
import capitalize from 'lodash.capitalize';
import NavIcon from './NavIcon';
import Box from 'components/elements/Box';

interface IMobileNavProps {
  activeTopic: any;
  getTopicUrl: (topic: any) => string;
}

const getTopicLabel = (
  activeTopic: string,
  activeTopicLabel: string | undefined,
  entityType: string
): string => {
  return `${activeTopic === 'overview' ? `${capitalize(entityType)} ` : ''} ${
    activeTopicLabel || ''
  }`;
};

const MobileNav: React.FC<IMobileNavProps> = (props) => {
  const { activeTopic, getTopicUrl } = props;

  const { entityType } = useContext(EntityContext);

  const [isOpen, setOpen] = useState(false);
  const transitionDuration = 300;
  const transitionStyles = { transitionDuration: `${transitionDuration}ms` };
  const { getCollapseProps, getToggleProps } = useCollapse({
    expandStyles: transitionStyles,
    collapseStyles: transitionStyles,
    isOpen,
  });

  function onClick(): void {
    setOpen(!isOpen);
  }

  const activeTopicLabel = topicsWithSubTopics.find(
    (t) => t.value === activeTopic
  )?.label;

  return (
    <Nav>
      <Button {...getToggleProps({ onClick })}>
        <ActiveTopic isOpen={isOpen}>
          {isOpen ? (
            'Close Menu'
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center">
              <NavIcon navElementName={activeTopic} />
              <span>{`${getTopicLabel(
                activeTopic,
                activeTopicLabel,
                entityType
              )}`}</span>
            </Box>
          )}
        </ActiveTopic>
        <CaretIcon aria-hidden isPointingUp={isOpen} />
      </Button>
      <List {...getCollapseProps()}>
        {topicsWithSubTopics.map((topic) => {
          if (topic.children) {
            return (
              <li key={topic.value}>
                <SubList
                  topic={topic}
                  activeTopic={activeTopic}
                  getTopicUrl={getTopicUrl}
                  transitionStyles={transitionStyles}
                  parentOnClick={onClick}
                  parentIsOpen={isOpen}
                  entityType={entityType}
                />
              </li>
            );
          }

          return (
            <li key={topic.value}>
              <Link to={getTopicUrl(topic.value)} onClick={onClick}>
                {topic.label}
              </Link>
            </li>
          );
        })}
      </List>
    </Nav>
  );
};

function SubList(props: any): JSX.Element {
  const {
    topic,
    getTopicUrl,
    activeTopic,
    transitionStyles,
    parentOnClick,
    parentIsOpen,
    entityType,
  } = props;
  const { value, children } = topic;

  const isActive = activeTopic === value;
  const [isOpen, setOpen] = useState(isActive);
  const { getCollapseProps, getToggleProps } = useCollapse({
    expandStyles: transitionStyles,
    collapseStyles: transitionStyles,
    isOpen,
  });

  /*
   * When the user opens the menu, the list of the active topic
   * is automatically opened. If the user than opens other lists,
   * clicks a link, and opens the main menu again — all the opened sub lists
   * are still open. This effects resets the other sub lists.
   */
  useEffect(() => {
    if (!parentIsOpen && !isActive) {
      setOpen(false);
    }
    if (!parentIsOpen && isActive) {
      setOpen(true);
    }
  }, [parentIsOpen, isActive]);

  function onClick(): void {
    setOpen(!isOpen);
  }

  let subTopics = children;
  if (
    topic?.value === 'overview' &&
    (entityType === 'project' || entityType === 'artist')
  ) {
    subTopics = topic.children?.filter(
      (topic: any) => topic.value !== 'certifications'
    );
  }

  if (entityType !== 'track' && topic?.value === 'charts') {
    subTopics = topic.children?.filter(
      (topic: any) => topic.value !== 'positionTracker'
    );
  }

  if (entityType !== 'artist' && topic?.value === 'social') {
    subTopics = topic.children?.filter(
      (topic: any) => topic.value !== 'linkfire'
    );
  }

  return (
    <>
      <SubButton {...getToggleProps({ onClick })}>
        <NavIcon navElementName={value} />
        <span>{getTopicLabel(value, topic?.label, entityType)}</span>
        <CaretIcon aria-hidden isPointingUp={isOpen} />
      </SubButton>
      <StyledSubList {...getCollapseProps({ isOpen })}>
        {subTopics.map((child: any) => (
          <li key={child.value}>
            <Link
              onClick={parentOnClick}
              to={getTopicUrl(`${topic.value}/${child.value}`)}
            >
              <WithNewLabel isNew={false}>{child.label}</WithNewLabel>
            </Link>
          </li>
        ))}
      </StyledSubList>
    </>
  );
}

export default MobileNav;
